import { useEffect, useRef, useState } from "react";
import { translate } from "../../shared/translations";
import { useReport } from "../data/mutations";
import type { Person } from "../../shared/types";
import { t } from "../i18n";
import { Avatar, Modal } from "../ui/components";
import CaretLeft from "~icons/ph/caret-left";
import EyeSlash from "~icons/ph/eye-slash";
import More from "~icons/ph/dots-three-circle";
import PhoneX from "~icons/ph/phone-x";
import ShieldWarning from "~icons/ph/shield-warning";
import SmileyAngry from "~icons/ph/smiley-angry";

const threats = "تهديد أو شعور بعدم الأمان",
  other = "سبب آخر";
const reasons = [
  ["إزعاج أو إهانات", SmileyAngry],
  [threats, ShieldWarning],
  ["محتوى جنسي أو غير لائق", EyeSlash],
  ["رسائل أو مكالمات مزعجة", PhoneX],
  [other, More],
] as const;
type Reason = (typeof reasons)[number][0];

export function ReportDialog({
  person,
  canBlock,
  onClose,
}: {
  person: Pick<Person, "id" | "name">;
  canBlock: boolean;
  onClose: () => void;
}) {
  const report = useReport();
  const pending = report.isPending,
    sent = report.isSuccess,
    error = report.error?.message ?? "";
  const onReport = (reason: string, block: boolean) =>
    report.mutate({ peer: person.id, reason, block });
  const [reason, setReason] = useState<Reason | null>(null),
    [details, setDetails] = useState(""),
    [block, setBlock] = useState(false);
  const note = details.trim(),
    view = sent ? "sent" : reason ? "details" : "reason";
  // Each step replaces the control that had focus, so focus moves to its title.
  const heading = useRef<HTMLHeadingElement>(null),
    shownView = useRef(view);
  useEffect(() => {
    if (shownView.current !== view) heading.current?.focus();
    shownView.current = view;
  }, [view]);
  return (
    <Modal
      title={t("الإبلاغ عن {name}", { name: person.name })}
      className="report-dialog"
      onClose={() => !pending && onClose()}
    >
      <div className="profile-view">
        <Avatar person={person} large />
        <h2 ref={heading} tabIndex={-1}>
          {view === "sent"
            ? t("شكرًا لإخبارنا")
            : reason
              ? t(reason)
              : t("ماذا حدث مع {name}؟", { name: person.name })}
        </h2>
        {view === "reason" && (
          <p className="modal-description">
            {t("لن يعرف {name} أن البلاغ منك.", { name: person.name })}
          </p>
        )}
      </div>
      {view === "sent" ? (
        <div className="report-step">
          <p className="report-sent-text">
            {t("سنراجع ما حدث، ولن يعرف {name} أن البلاغ منك.", {
              name: person.name,
            })}
          </p>
          {block && (
            <p className="report-sent-text">
              {t("تم حظر {name}، ولن تصلك مكالمات أو رسائل من هذا الحساب.", {
                name: person.name,
              })}
            </p>
          )}
          <div className="confirm-actions">
            <button className="button" type="button" onClick={onClose}>
              {t("تم")}
            </button>
          </div>
        </div>
      ) : reason ? (
        <form
          className="report-step"
          onSubmit={(event) => {
            event.preventDefault();
            if (pending || (reason === other && !note)) return;
            // Administrators read reports in English, with the note as written.
            const type = translate(reason, "en");
            onReport(note ? `${type}\n\n${note}` : type, canBlock && block);
          }}
        >
          {reason === threats && (
            <p className="report-note">
              {t("إذا كنت في خطر الآن، فاتصل برقم الطوارئ في منطقتك.")}
            </p>
          )}
          <label>
            {t(
              reason === other
                ? "أخبرنا بما حدث"
                : "هل تود إضافة شيء؟ (اختياري)",
            )}
            <textarea
              required={reason === other}
              maxLength={900}
              value={details}
              disabled={pending}
              onChange={(event) => setDetails(event.target.value)}
            />
          </label>
          {canBlock && (
            <label className="setting-row">
              <span className="setting-text">
                <strong id="report-block-label">
                  {t("حظر {name}", { name: person.name })}
                </strong>
                <small id="report-block-help">
                  {t("لن تصلك مكالمات أو رسائل من هذا الحساب.")}
                </small>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="switch"
                checked={block}
                aria-checked={block}
                aria-labelledby="report-block-label"
                aria-describedby="report-block-help"
                disabled={pending}
                onChange={(event) => setBlock(event.target.checked)}
              />
            </label>
          )}
          {error && (
            <p role="alert" className="form-error">
              {t(error)}
            </p>
          )}
          <div className="confirm-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => setReason(null)}
              disabled={pending}
            >
              {t("رجوع")}
            </button>
            <button
              className="button"
              disabled={pending || (reason === other && !note)}
            >
              {t("إرسال البلاغ")}
            </button>
          </div>
        </form>
      ) : (
        <div className="report-step">
          {reasons.map(([label, Icon]) => (
            <button
              key={label}
              type="button"
              className="setting-row setting-link"
              onClick={() => setReason(label)}
            >
              <Icon width={20} height={20} aria-hidden="true" />
              <strong className="setting-text">{t(label)}</strong>
              <CaretLeft
                className="directional"
                width={16}
                height={16}
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
